<template>
  <div>
    <IHTechnicalPlaceSelect
      v-if="siteId != null"
      v-model:technical-category-id="action.technicalCategoryId"
      v-model:technical-place-id="action.technicalPlaceId"
      :site-id="siteId"
    />

    <EventActionFieldRowComponent :label="t('license.ih.ih.responsibleIHOrder')">
      <InputRow
        v-slot="{ validation: { valid } }"
        v-model="action.assigneeId"
        name="assigneeId"
        required
      >
        <InputText
          v-if="!action.claimed"
          class="text-error w-100 p-valid"
          :value="t('license.ih.ih.hideResponsibleModal')"
          @click="action.claimed = true"
        />
        <SearchSelect
          v-else
          v-model="action.assigneeId"
          :state="valid"
          :mode="SelectMode.USER"
          :filters="
            siteId == null
              ? undefined
              : {
                  hasRightsOnSite: {
                    siteIds: [siteId],
                    rights: SQDC_ASSING_MINIMUM_RIGHTS,
                  },
                }
          "
          class="w-100"
          :placeholder="t('components.TaskModal.userPlaceholder').toString()"
          label="preferredName"
        />
      </InputRow>
    </EventActionFieldRowComponent>

    <EventActionFieldRowComponent :label="t('components.issueModal.maintenanceDescription')">
      <InputRow
        v-slot="{ field: inputField, validation: { valid } }"
        v-model="action.description"
        name="description"
        required
        :rules="[minLength(1)]"
      >
        <InputTextArea
          v-bind="inputField"
          id="FieldsIHOrderModalComponentDescription"
          v-model="action.description"
          data-testid="FieldsIHOrderModalComponentDescription"
          class="w-100"
          auto-resize
          rows="4"
          :state="valid"
          @keydown.meta.enter="$emit('save', $event)"
          @keydown.ctrl.enter="$emit('save', $event)"
        />
      </InputRow>
    </EventActionFieldRowComponent>

    <hr />

    <EventActionFieldRowComponent
      :label="
        repTimeNoRange ? t('components.TaskModal.deadline') : t('components.issueModal.repTime')
      "
    >
      <GridRow>
        <GridCol cols="9" class="pr-0">
          <InputRow
            v-if="!ihConfig?.repTimeNoRange"
            v-slot="{ field: inputField, validation: { valid } }"
            :model-value="repTime"
            name="repTime"
            required
            :rules="[positiveDateRange()]"
          >
            <DatePickerComponentNew
              v-bind="inputField"
              v-model="repTime"
              :clearable="false"
              :range="true"
              :state="valid"
            />
          </InputRow>
          <InputRow
            v-else
            v-slot="{ field: inputField, validation: { valid } }"
            :model-value="repTimeDay"
            name="repTime"
            required
          >
            <DatePickerComponentNew
              v-bind="inputField"
              v-model="repTimeDay"
              :clearable="false"
              :type="DatePickerType.DAY"
              :state="valid"
            />
          </InputRow>
        </GridCol>
        <GridCol>
          <InputGroup>
            <InputText v-model="duration" disabled></InputText>
            <InputGroupAddon class="p-0">
              {{ t("license.ih.ih.hour") }}
            </InputGroupAddon>
          </InputGroup>
        </GridCol>
      </GridRow>
    </EventActionFieldRowComponent>

    <EventActionFieldRowComponent :label="t('components.issueModal.plannedTime')">
      <InputGroup>
        <LISAInputNumber v-model="action.plannedTime" />
        <InputGroupAddon class="p-0">
          {{ t("license.ih.ih.hour") }}
        </InputGroupAddon>
      </InputGroup>
    </EventActionFieldRowComponent>

    <hr class="mt-0" />

    <EventActionFieldRowComponent :label="t('components.issueModal.internalResources')">
      <InputRow
        v-slot="{ validation: { valid } }"
        v-model="action.internalResourcesIds"
        name="internalResourcesIds"
      >
        <SearchSelect
          v-model="action.internalResourcesIds"
          :mode="SelectMode.USER"
          :filters="
            siteId == null
              ? undefined
              : {
                  hasRightsOnSite: {
                    siteIds: [siteId],
                    rights: SQDC_ASSING_MINIMUM_RIGHTS,
                  },
                }
          "
          class="w-100"
          :multiple="true"
          :state="valid"
          label="preferredName"
        />
      </InputRow>
    </EventActionFieldRowComponent>

    <EventActionFieldRowComponent :label="t('components.issueModal.equipments')">
      <SearchSelect
        v-model="action.equipmentsIds"
        :mode="SelectMode.IHEQUIPMENT"
        :state="undefined"
        data-testId="equipment"
        class="w-100"
        :multiple="true"
        label="name"
      />
    </EventActionFieldRowComponent>

    <EventActionFieldRowComponent :label="t('license.ih.ih.status')">
      <GridRow>
        <div class="flex flex-wrap">
          <StatusSelectComponent
            v-model:status="action.status"
            class="flex justify-content-start pt-2 pl-2"
          />
          <div class="pt-1">
            <FMFileInlineUploadPreview v-model:files-ids="action.fileIds" />
          </div>
        </div>
      </GridRow>
    </EventActionFieldRowComponent>
  </div>
</template>

<script setup lang="ts">
import { minLength, positiveDateRange } from "@/modules/formValidation";
import InputRow from "@/components/shared/form/InputRow.vue";
import { computed, inject, type PropType, onMounted } from "vue";
import { DateTime } from "luxon";
import InputText from "primevue/inputtext";
import InputTextArea from "primevue/textarea";
import { useTranslation } from "@/language/translation";
import StatusSelectComponent from "@/components/event-action/task/StatusSelectComponent.vue";

import { TechnicalPlaceAndCategoryInjectionKey } from "@/views/event-action/EventActionBoardInjectionKeys";
import IHTechnicalPlaceSelect from "./IHTechnicalPlaceSelect.vue";
import GridRow from "@/components/shared/utility/GridRow.vue";
import GridCol from "@/components/shared/utility/GridCol.vue";
import DatePickerComponentNew from "@/components/shared/DatePickerComponentNew.vue";
import EventActionFieldRowComponent from "@/components/event-action/EventActionFieldRowComponent.vue";
import { DatePickerType } from "@/components/shared/DatePickerComponentHelper";
import LISAInputNumber from "@/components/primevueWrapper/LISAInputNumber.vue";
import InputGroupAddon from "primevue/inputgroupaddon";
import InputGroup from "primevue/inputgroup";
import { useIHConfig, useSpecifiedBoardContext } from "@/modules/sqdc/helper/EABConfigHelper";
import SearchSelect from "@/components/shared/searchSelects/SearchSelect.vue";
import { SelectMode } from "@/components/shared/searchSelects/SelectDefinitions";
import { SQDC_ASSING_MINIMUM_RIGHTS } from "@/components/event-action/task/helper/SQDCHelper";
import FMFileInlineUploadPreview from "@/modules/filemanagement/components/FMFileInlineUploadPreview.vue";
import type { IHOrderEditType } from "@/components/event-action/task/helper/DefaultTask";

const props = defineProps({
  action: {
    type: Object as PropType<IHOrderEditType>,
    required: true,
  },
  boardId: {
    type: Number,
    required: true,
  },
});
defineEmits(["save"]);
const i18n = useTranslation();

const { siteId } = useSpecifiedBoardContext(computed(() => props.boardId));

const injectedTechnicalPlaceAndCategory = inject(
  TechnicalPlaceAndCategoryInjectionKey,
  computed(() => undefined),
);

const repTime = computed<[DateTime, DateTime] | undefined>({
  get() {
    if (props.action.repTime.length <= 0) {
      return undefined;
    }
    return props.action.repTime.map((element: string) => DateTime.fromISO(element)) as [
      DateTime,
      DateTime,
    ];
  },
  set(value) {
    props.action.repTime = value ? value.map((el) => el.toISO()) : [];
  },
});

const duration = computed(() => {
  if (!repTime.value) return "0";
  return i18n.n(
    Number(repTime.value[1].diff(repTime.value[0], "hours").toObject().hours?.toFixed(2)),
  );
});

const { resultIHConfig: ihConfig } = useIHConfig(siteId);

const repTimeNoRange = computed(() => {
  return ihConfig.value?.repTimeNoRange ?? false;
});

const repTimeDay = computed({
  get(): DateTime | undefined {
    if (repTime.value?.[1] == null) {
      return undefined;
    }
    return repTime.value[1];
  },
  set(val: DateTime | undefined) {
    if (val == null) {
      repTime.value = undefined;
    } else {
      repTime.value = [val.startOf("day"), val.endOf("day")];
    }
  },
});

onMounted(() => {
  if (
    props.action.technicalPlaceId == null &&
    injectedTechnicalPlaceAndCategory?.value?.technicalPlaceId
  ) {
    props.action.technicalPlaceId = injectedTechnicalPlaceAndCategory?.value?.technicalPlaceId;
  }
  if (
    props.action.technicalCategoryId == null &&
    injectedTechnicalPlaceAndCategory?.value?.technicalCategoryId
  ) {
    props.action.technicalCategoryId =
      injectedTechnicalPlaceAndCategory?.value?.technicalCategoryId;
  }
});

const { t } = useTranslation();
</script>

<style lang="scss" scoped></style>
